/**
 * ShopTheRoomDetails UI Component
 * Handles client-side hydration of inventory/price and renders room details UI.
 * @param {Object} props
 * @param {Object} props.roomData - Normalized room and SKU details from SSR
 * @return {JSX.Element}
 */
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getProductInventory } from "@/services/productServices"
import RoomDetailsUI from "@/components/ShopTheRoomDetails/ui/RoomDetailsUI"
import styles from "@/components/ShopTheRoomDetails/styles/ShopTheRoomDetails.module.scss"
import {
  shareaddToCart as addToCartHelper,
  getProductsBySku as getSkuPricebyCT,
} from "@/components/sharedCart/shareCartHelper"
import {
  mergeInventoryWithSkusAndPrices,
  normalizeSkuPricesResponse,
} from "@/responseProcessors/shopTheRoomResponseProcessor"
import {
  selectGenericState,
  showPreviewCart,
  showToast,
} from "@/store/features/genericSlice"
import { getUserPersona } from "@/utils/helper"
import { getStock } from "@/utils/cart"
import useShopTheRoomDetailsi18n from "@/i18n/useShopTheRoomDetailsi18n"
import { getConfig } from "@/constants/config"
import { pageUrl, SHOP_THE_ROOM } from "@/constants/index"
import {
  datlayerForBreadCrump,
  datlayerForProductAddToCart,
  datlayerForProductclick,
} from "@/components/ShopTheRoomDetails/Analytics/index"

const ShopTheRoomDetails = ({ roomData = {} }) => {
  const [roomState, setRoomState] = useState(roomData)
  const [skuList, setSkuList] = useState(roomData.skuList || [])
  const [inventoryHydrated, setInventoryHydrated] = useState(false)
  const dispatch = useDispatch()
  const staticText = useShopTheRoomDetailsi18n()
  const persona = getUserPersona()

  useEffect(() => {
    // Only hydrate inventory if SSR data is present
    if (roomData.skuList && roomData.skuList.length > 0) {
      fetchAndMergeInventory()
    }
    return () => {
      document.body.classList.remove("nav-transparent-light-theme") // Removes class on unmount
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomData])

  /**
   * Fetch and merge inventory for SKUs on the client
   */
  async function fetchAndMergeInventory() {
    const { internationalization: { currencyCode = "" } = {} } =
      await getConfig()
    const { skuList } = roomData
    if (!skuList.length) {
      setRoomState(roomData)
      setSkuList([])
      setInventoryHydrated(true)
      return
    }

    const skuKeys = skuList.map(sku => sku.sku_s)
    const { inventories = [] } = await getProductInventory(skuKeys)

    // fetching all skus price from CT api
    const ctSkusData = await getSkuPricebyCT(skuKeys)
    const ctSkusPrices = normalizeSkuPricesResponse(
      ctSkusData,
      currencyCode,
      persona,
      skuKeys
    )
    const mergedSkuList = mergeInventoryWithSkusAndPrices(
      skuList,
      inventories,
      ctSkusPrices
    )

    setRoomState(roomData)
    setSkuList(mergedSkuList)
    setInventoryHydrated(true)
  }

  const {
    globalConfig: {
      presetConfigs: { shopTheRoomDetails = {} } = {},
      general: { swatchUrl = "" } = {},
      apiEndPointData: { map: { skuPrepend = "" } = {} } = {},
      apiEndpoints: { stockFailureErrorMsg = "" } = {},
    } = {},
  } = useSelector(selectGenericState)

  /**
   * Handle Notify Me action
   * @param {string} pdpUrl - Product detail page URL
   * @param {string} sku - Product sku for the URL
   * @param {Object} data - Additional product data (optional)
   * @param {string} title - Product title for the URL
   * @return {void}
   */
  const handleNotifyMe = (pdpUrl = "", sku = "", data = {}, title = "") => {
    datlayerForProductclick(pdpUrl, sku, data, title, skuPrepend)
  }

  /**
   * Handle Add to Cart action
   * @param {string} sku - SKU identifier of the product to add
   * @param {Object} data - Additional product data (optional)
   * @return {Promise<void>}
   */
  const handleAddToCart = async (sku, data) => {
    let result = skuList.map(item =>
      item.sku_s === sku ? { ...item, loading: true } : item
    )
    setSkuList(result)
    try {
      const { stock = 0, isError = false } = await getStock(sku)
      if (isError) {
        dispatch(
          showToast({
            isVisible: true,
            message: stockFailureErrorMsg,
          })
        )
        return
      }
      if (stock > 0) {
        const selectedSku = skuList.find(item => item.sku_s === sku)
        const quantity = selectedSku?.itemQuantity || 1
        const actions = [
          {
            action: "addLineItem",
            sku: sku,
            quantity,
            custom: {
              type: {
                key: "lineItemShipping",
              },
              fields: {
                salesChannel: "KOUS",
              },
            },
          },
        ]

        let totalAdded = 0
        actions.forEach(item => (totalAdded += item.quantity))
        addToCartHelper([...actions], "", (isFailed, res) => {
          if (!isFailed && res) {
            dispatch(showPreviewCart())
          }
        })
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
    }
    result = skuList.map(item =>
      item.sku_s === sku ? { ...item, loading: false } : item
    )
    setSkuList(result)
    datlayerForProductAddToCart(data, "add to cart", skuPrepend)
  }

  /**
   * Handle quantity change for a SKU
   * @param {string} sku - SKU identifier of the product
   * @param {number} qty - New quantity to set
   * @return {Promise<void>}
   */
  const handleChange = async (sku, qty) => {
    const data = [...skuList]
    let result = []
    result = data.map(item =>
      item.sku_s === sku ? { ...item, itemQuantity: qty, loading: true } : item
    )
    setSkuList(result)
    if (qty && qty !== 0) {
      const { stock = 0 } = await getStock(sku)
      if (qty > stock) {
        result = data.map(item =>
          item.sku_s === sku
            ? {
                ...item,
                itemQuantity: stock,
                stock: stock,
                showMsg: true,
                loading: false,
              }
            : item
        )
      } else {
        result = data.map(item =>
          item.sku_s === sku
            ? {
                ...item,
                currentInventory: stock - qty,
                itemQuantity: qty,
                stock: stock,
                showMsg: false,
                loading: false,
              }
            : item
        )
      }
    }
    setSkuList(result)
  }

  if (typeof window !== "undefined") {
    localStorage?.setItem("shopTheRoomSkuList", skuList)
  }

  return (
    <div className={styles.InspirationShopTheRoom}>
      <div className="shop-the-room-detail-page__breadcrumb">
        <nav aria-label="Breadcrumb">
          <ol>
            <li>
              <a
                aria-label={`Home Page`}
                className="shop-the-room-detail-page__breadcrumb-link"
                href={pageUrl}
                onClick={() => {
                  datlayerForBreadCrump("Home", pageUrl)
                }}
              >
                {" Home / "}
              </a>
            </li>
            <li>
              <a
                aria-label={`Home Page`}
                className="shop-the-room-detail-page__breadcrumb-link"
                href={`${pageUrl}${SHOP_THE_ROOM}`}
                onClick={() => {
                  datlayerForBreadCrump(
                    "home > Shop the Room",
                    `${pageUrl}${SHOP_THE_ROOM}`
                  )
                }}
              >
                {"Shop the Room"}
              </a>
            </li>
          </ol>
        </nav>
      </div>
      <RoomDetailsUI
        roomData={roomState}
        skuList={skuList}
        handleNotifyMe={handleNotifyMe}
        handleAddToCart={handleAddToCart}
        handleChange={handleChange}
        inventoryHydrated={inventoryHydrated}
        i18n={staticText}
        presetConfig={shopTheRoomDetails}
        swatchUrl={swatchUrl}
        skuPrepend={skuPrepend}
      />
    </div>
  )
}

export default ShopTheRoomDetails
